export type PartOfSpeechId = 'NOUN' | 'VERB' | 'ADJECTIVE' | 'OTHER';
export type VerbTypeId =
  | 'GODAN'
  | 'ICHIDAN'
  | 'IRREGULAR'
  | 'SURU'
  | 'KURU'
  | 'ARU'
  | 'ICHIDAN_SPECIAL'
  | 'GODAN_KU'
  | 'GODAN_KU_SPECIAL'
  | 'GODAN_GU'
  | 'GODAN_SU'
  | 'GODAN_TSU'
  | 'GODAN_NU'
  | 'GODAN_BU'
  | 'GODAN_MU'
  | 'GODAN_RU'
  | 'GODAN_RU_IRREGULAR'
  | 'GODAN_U'
  | 'GODAN_U_SPECIAL'
  | 'GODAN_ARU'
  | 'GODAN_URU'
  | 'ZURU'
  | 'IRREGULAR_NU'
  | 'IRREGULAR_RU';
export type VerbFormId =
  | 'PRESENT_DICTIONARY'
  | 'PRESENT_POLITE'
  | 'PRESENT_DICTIONARY_NEGATIVE'
  | 'PRESENT_POLITE_NEGATIVE'
  | 'PAST_PLAIN'
  | 'PAST_POLITE'
  | 'PAST_PLAIN_NEGATIVE'
  | 'PAST_POLITE_NEGATIVE'
  | 'TE_FORM'
  | 'TE_FORM_NEGATIVE'
  | 'POTENTIAL'
  | 'POTENTIAL_NEGATIVE'
  | 'PASSIVE'
  | 'PASSIVE_NEGATIVE'
  | 'CAUSATIVE'
  | 'CAUSATIVE_NEGATIVE'
  | 'CAUSATIVE_PASSIVE'
  | 'CAUSATIVE_PASSIVE_NEGATIVE'
  | 'IMPERATIVE'
  | 'IMPERATIVE_NEGATIVE';

export interface VocabularyOption<T> {
  value: T;
  label: string;
}

export const PART_OF_SPEECH_OPTIONS: VocabularyOption<PartOfSpeechId>[] = [
  { value: 'NOUN', label: 'Noun' },
  { value: 'VERB', label: 'Verb' },
  { value: 'ADJECTIVE', label: 'Adjective' },
  { value: 'OTHER', label: 'Other' },
];

export const VERB_TYPE_OPTIONS: VocabularyOption<VerbTypeId>[] = [
  { value: 'ICHIDAN', label: 'Ichidan (一段)' },
  { value: 'ICHIDAN_SPECIAL', label: 'Ichidan Special (一段特殊)' },
  { value: 'GODAN_KU', label: 'Godan -ku (五段・カ行)' },
  { value: 'GODAN_KU_SPECIAL', label: 'Godan -ku Special (五段・カ行特殊)' },
  { value: 'GODAN_GU', label: 'Godan -gu (五段・ガ行)' },
  { value: 'GODAN_SU', label: 'Godan -su (五段・サ行)' },
  { value: 'GODAN_TSU', label: 'Godan -tsu (五段・タ行)' },
  { value: 'GODAN_NU', label: 'Godan -nu (五段・ナ行)' },
  { value: 'GODAN_BU', label: 'Godan -bu (五段・バ行)' },
  { value: 'GODAN_MU', label: 'Godan -mu (五段・マ行)' },
  { value: 'GODAN_RU', label: 'Godan -ru (五段・ラ行)' },
  { value: 'GODAN_RU_IRREGULAR', label: 'Godan -ru Irregular (五段・ラ行特殊)' },
  { value: 'GODAN_U', label: 'Godan -u (五段・ウ行)' },
  { value: 'GODAN_U_SPECIAL', label: 'Godan -u Special (五段・ウ行特殊)' },
  { value: 'GODAN_ARU', label: 'Godan -aru Special (五段・アル特殊)' },
  { value: 'GODAN_URU', label: 'Godan -Uru Old (五段・ウル特殊)' },
  { value: 'GODAN', label: 'Godan (五段) - Default' },
  { value: 'KURU', label: 'Kuru (来る)' },
  { value: 'SURU', label: 'Suru (する)' },
  { value: 'ZURU', label: 'Zuru (ずる)' },
  { value: 'IRREGULAR_NU', label: 'Irregular -nu (不規則・ヌ)' },
  { value: 'IRREGULAR_RU', label: 'Irregular -ru (不規則・ル)' },
  { value: 'ARU', label: 'Aru (ある)' },
  { value: 'IRREGULAR', label: 'Irregular (不規則)' },
];

/**
 * Maps a POS code to a VerbTypeId.
 * @param posCode The POS code (e.g., "v1", "v5k", "vk")
 * @returns The corresponding VerbTypeId, or "GODAN" as fallback
 */
export const getVerbTypeFromPosCode = (posCode?: string): VerbTypeId => {
  if (!posCode) {
    return 'GODAN';
  }
  
  const posCodeToVerbType: Record<string, VerbTypeId> = {
    'v1': 'ICHIDAN',
    'v1-s': 'ICHIDAN_SPECIAL',
    'v5k': 'GODAN_KU',
    'v5k-s': 'GODAN_KU_SPECIAL',
    'v5g': 'GODAN_GU',
    'v5s': 'GODAN_SU',
    'v5t': 'GODAN_TSU',
    'v5n': 'GODAN_NU',
    'v5b': 'GODAN_BU',
    'v5m': 'GODAN_MU',
    'v5r': 'GODAN_RU',
    'v5r-i': 'GODAN_RU_IRREGULAR',
    'v5u': 'GODAN_U',
    'v5u-s': 'GODAN_U_SPECIAL',
    'v5aru': 'GODAN_ARU',
    'v5uru': 'GODAN_URU',
    'vk': 'KURU',
    'vs-i': 'SURU',
    'vz': 'ZURU',
    'vn': 'IRREGULAR_NU',
    'vr': 'IRREGULAR_RU',
  };
  
  return posCodeToVerbType[posCode] || 'GODAN';
};

const createLabelLookup = <T extends string>(options: VocabularyOption<T>[]) =>
  options.reduce<Record<T, string>>((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {} as Record<T, string>);

const partOfSpeechLabelLookup = createLabelLookup(PART_OF_SPEECH_OPTIONS);
const verbTypeLabelLookup = createLabelLookup(VERB_TYPE_OPTIONS);

export const getPartOfSpeechLabel = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }
  return partOfSpeechLabelLookup[value as PartOfSpeechId];
};

export const getVerbTypeLabel = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }
  return verbTypeLabelLookup[value as VerbTypeId];
};

interface VerbFormInfo {
  id: VerbFormId;
  label: string;
  description?: string;
}

const VERB_FORM_INFOS: VerbFormInfo[] = [
  { id: 'PRESENT_DICTIONARY', label: 'Dictionary', description: 'Affirmative' },
  { id: 'PRESENT_DICTIONARY_NEGATIVE', label: 'Dictionary', description: 'Negative' },
  { id: 'PRESENT_POLITE', label: 'Polite', description: 'Affirmative' },
  { id: 'PRESENT_POLITE_NEGATIVE', label: 'Polite', description: 'Negative' },
  { id: 'PAST_PLAIN', label: 'Past (plain)', description: 'Affirmative' },
  { id: 'PAST_PLAIN_NEGATIVE', label: 'Past (plain)', description: 'Negative' },
  { id: 'PAST_POLITE', label: 'Past (polite)', description: 'Affirmative' },
  { id: 'PAST_POLITE_NEGATIVE', label: 'Past (polite)', description: 'Negative' },
  { id: 'TE_FORM', label: 'Te-form', description: 'Affirmative' },
  { id: 'TE_FORM_NEGATIVE', label: 'Te-form', description: 'Negative' },
  { id: 'POTENTIAL', label: 'Potential', description: 'Affirmative' },
  { id: 'POTENTIAL_NEGATIVE', label: 'Potential', description: 'Negative' },
  { id: 'PASSIVE', label: 'Passive', description: 'Affirmative' },
  { id: 'PASSIVE_NEGATIVE', label: 'Passive', description: 'Negative' },
  { id: 'CAUSATIVE', label: 'Causative', description: 'Affirmative' },
  { id: 'CAUSATIVE_NEGATIVE', label: 'Causative', description: 'Negative' },
  { id: 'CAUSATIVE_PASSIVE', label: 'Causative passive', description: 'Affirmative' },
  { id: 'CAUSATIVE_PASSIVE_NEGATIVE', label: 'Causative passive', description: 'Negative' },
  { id: 'IMPERATIVE', label: 'Imperative', description: 'Affirmative' },
  { id: 'IMPERATIVE_NEGATIVE', label: 'Imperative', description: 'Negative' },
];

export const VERB_FORM_ORDER = VERB_FORM_INFOS.map((info) => info.id);

const verbFormInfoLookup = VERB_FORM_INFOS.reduce<Record<string, VerbFormInfo>>((acc, info) => {
  acc[info.id] = info;
  return acc;
}, {});

export const getVerbFormInfo = (value: string): VerbFormInfo => {
  const info = verbFormInfoLookup[value as VerbFormId];
  if (info) {
    return info;
  }
  return {
    id: value as VerbFormId,
    label: value.replace(/_/g, ' ').toLowerCase(),
  };
};

export interface ConjugationRow {
  /** Stable key for the row (label of the affirmative form). */
  key: string;
  /** Human-readable form label, e.g. "Dictionary". */
  label: string;
  /** Affirmative conjugation value (may be undefined if missing). */
  affirmative?: string;
  /** Negative conjugation value (may be undefined if missing). */
  negative?: string;
}

/**
 * Groups conjugation entries so that the affirmative and negative variants of
 * the same verb form share a single row. Rows are returned in the same order
 * as VERB_FORM_ORDER, based on the position of the affirmative form. Forms
 * whose label is not recognized fall back to their own row.
 */
export const groupConjugationsByForm = (
  conjugations: Record<string, string | undefined | null> | undefined,
): ConjugationRow[] => {
  if (!conjugations) {
    return [];
  }

  const rowsByLabel = new Map<string, ConjugationRow & { order: number }>();
  const extras: (ConjugationRow & { order: number })[] = [];

  for (const [form, rawValue] of Object.entries(conjugations)) {
    const value = rawValue ?? undefined;
    if (!value) {
      continue;
    }
    const info = getVerbFormInfo(form);
    const isNegative = form.endsWith('_NEGATIVE');
    const orderIndex = VERB_FORM_ORDER.indexOf(form as VerbFormId);
    const safeOrder = orderIndex === -1 ? Number.MAX_SAFE_INTEGER : orderIndex;

    // Use the label as grouping key so PRESENT_DICTIONARY and
    // PRESENT_DICTIONARY_NEGATIVE end up on the same row.
    const key = info.label;
    const existing = rowsByLabel.get(key);
    if (existing) {
      if (isNegative) {
        existing.negative = value;
      } else {
        existing.affirmative = value;
        // Affirmative defines the row's sort order.
        existing.order = Math.min(existing.order, safeOrder);
      }
      continue;
    }

    const row: ConjugationRow & { order: number } = {
      key,
      label: info.label,
      affirmative: isNegative ? undefined : value,
      negative: isNegative ? value : undefined,
      order: safeOrder,
    };

    if (info.label) {
      rowsByLabel.set(key, row);
    } else {
      extras.push(row);
    }
  }

  return [...Array.from(rowsByLabel.values()), ...extras]
    .sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return a.label.localeCompare(b.label);
    })
    .map(({ order: _order, ...row }) => row);
};

